import { useFormik } from "formik";
import { useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../../components/buttons/button";
import { ContainerCard } from "../../../../../components/cards/containerCard";
import { FormCard } from "../../../../../components/cards/formCard";
import { InformationCard } from "../../../../../components/cards/infoCard";
import { CaretDown } from "../../../../../components/caret";
import { Icon } from "../../../../../components/icons/icon";
import { DropdownNew } from "../../../../../components/inputs/dropdownNew";
import { TextInput } from "../../../../../components/inputs/textInput";
import { TitleSubLoader } from "../../../../../components/loaders/titleSubLoader";
import { PageHeader } from "../../../../../components/typography/pageHeader";
import { TitleSub } from "../../../../../components/typography/titleSub";
import {
  parseStringToNumber,
  removeElementAtIndex,
  splitCamelCase,
} from "../../../../../helperFunctions";
import {
  useGetDefaultMembershipFormQuery,
  useGetMembershipFormFieldTypesQuery,
  useGetMembershipFormQuery,
  useUpdateMembershipformMutation,
} from "../../../../../redux/features/cooperative/cooperative.api";
import {
  CooperativeMembershipForm,
  CooperativeMembershipFormFieldType,
  SingleCooperativeMembershipFormField,
} from "../../../../../redux/features/cooperative/cooperative.type";
import { useAppSelector } from "../../../../../redux/hooks";
import {
  MEMBERSHIP_FORM_FIELD_ICONS_MAP,
  MEMBERSHIP_FORM_FIELD_TITLE_MAP,
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../../utils/constants";
import {
  addMembershipFormFieldSchema,
  membershipFormSchema,
} from "../../../../../validationSchema";

const AddFieldForm = ({
  onSave,
}: {
  onSave: (val: SingleCooperativeMembershipFormField) => void;
}) => {
  const { data: fieldTypes, isFetching: fetchingFieldTypes } =
    useGetMembershipFormFieldTypesQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const {
    values,
    handleBlur,
    handleChange,
    resetForm,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
  } = useFormik({
    initialValues: {
      fieldName: "",
      fieldType: "text",
    } as {
      fieldName: string;
      fieldType: CooperativeMembershipFormFieldType;
    },
    validationSchema: addMembershipFormFieldSchema,
    onSubmit: (data) => {
      const newFormData: SingleCooperativeMembershipFormField = {
        question: data.fieldName,
        fieldType: data?.fieldType,
        option: [],
        isDefault: false,
      };

      onSave(newFormData);

      resetForm();
    },
  });

  return fetchingFieldTypes ? (
    <div className="mt-[32px] grid grid-cols-2 gap-3 px-4 md:px-8">
      <Skeleton
        width={"100%"}
        height={48}
        baseColor={SKELETON_BASE_COLOR}
        highlightColor={SKELETON_HIGHLIGHT_COLOR}
        className="rounded-lg"
      />

      <Skeleton
        width={"100%"}
        height={48}
        baseColor={SKELETON_BASE_COLOR}
        highlightColor={SKELETON_HIGHLIGHT_COLOR}
        className="rounded-lg"
      />
    </div>
  ) : (
    <form className="px-4 md:px-8 mt-[32px]" onSubmit={handleSubmit}>
      <div className="w-full flex flex-row gap-3">
        <TextInput
          type="text"
          name={"fieldName"}
          value={values?.fieldName}
          placeholder="E.g Address, Means of ID"
          onChange={handleChange}
          onBlur={handleBlur}
          hasIcon={false}
          errors={
            errors?.fieldName && touched?.fieldName ? errors?.fieldName : ""
          }
          inputClass=""
          className=""
          autoComplete="off"
          hasTitle
          title="Field Name"
          containerClassName="w-full"
        />

        <DropdownNew
          menuItems={fieldTypes?.data?.map((f) => {
            return {
              name: MEMBERSHIP_FORM_FIELD_TITLE_MAP[f[0]],
              value: f[0],
              onClick: () => {
                setFieldValue("fieldType", f[0]);
              },
            };
          })}
          className="w-full max-w-[209px]"
        >
          <button type="button" className="text-left">
            <TextInput
              type="text"
              name={"fieldType"}
              value={MEMBERSHIP_FORM_FIELD_TITLE_MAP[values.fieldType]}
              placeholder="Choose type"
              onChange={handleChange}
              onBlur={handleBlur}
              hasIcon={true}
              errors={
                errors?.fieldType && touched?.fieldType ? errors?.fieldType : ""
              }
              inputClass=""
              className="!bg-white !border-pry-dark-green-300/40"
              autoComplete="off"
              hasTitle
              title="Field Type"
              readOnly
              containerClassName=""
              iconSvg={MEMBERSHIP_FORM_FIELD_ICONS_MAP[values.fieldType]}
              customIcon={<CaretDown />}
              customIconPosition="right"
            />
          </button>
        </DropdownNew>
      </div>

      <Button
        text="Add Field"
        icon="plus"
        type="submit"
        className="max-w-[104px] md:!py-2 mt-3 ml-auto"
        loading={false}
        disabled={false}
        variation="primary-dark"
      />
    </form>
  );
};

export const MembershipSettingsEdit = () => {
  const navigate = useNavigate();

  const { onboardingCompletionStatus } = useAppSelector((state) => {
    return state?.auth;
  });

  const { data: defaultFormFields, isFetching: fetchingdefaultFormFields } =
    useGetDefaultMembershipFormQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const { data: membershipForm, isFetching: fetchingMembershipform } =
    useGetMembershipFormQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const [updateForm, { isLoading: updatingForm }] =
    useUpdateMembershipformMutation();

  const {
    values,
    handleBlur,
    isValid,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    handleChange,
  } = useFormik({
    initialValues: {
      fee: "",
      form: [],
    } as { fee: string; form: SingleCooperativeMembershipFormField[] },
    enableReinitialize: false,
    validationSchema: membershipFormSchema,
    onSubmit: async (data) => {
      const newFields = data?.form?.filter((item) => {
        return !item?.isDefault;
      });

      const payload: CooperativeMembershipForm = {
        howMany: newFields.length,
        fee: parseStringToNumber(data?.fee) ?? 0,
        form: newFields,
      };

      const resp = await updateForm(payload);

      if (resp.data?.success) {
        navigate("/app/settings?tab=membership_settings");
      }
    },
  });

  useEffect(() => {
    setFieldValue("form", [
      ...(membershipForm?.data?.Field ?? []).map((v) => ({
        question: v.question,
        fieldType: v.fieldType,
        option: [],
        isDefault: false,
      })),
      ...(defaultFormFields?.data ?? []).map((v) => ({
        question: v.question,
        fieldType: v.fieldType,
        option: [],
        isDefault: true,
      })),
    ]);

    setFieldValue("fee", membershipForm?.data?.Form?.Fee ?? "");
    // eslint-disable-next-line
  }, [membershipForm, defaultFormFields, onboardingCompletionStatus]);

  return (
    <div>
      <PageHeader
        pageTitle="Membership Settings"
        hasBack
        onBackClick={() => {
          navigate("/app/settings?tab=membership_settings");
        }}
        hasRightChildren
        rightChildren={
          <Button
            type="button"
            variation="primary"
            className="!min-w-[82px] md:!min-w-[107px] !w-fit"
            size="medium"
            text="Save"
            onClick={() => {
              handleSubmit();
            }}
            loading={updatingForm}
            disabled={updatingForm || !isValid}
          />
        }
        className="max-md:hidden"
      />

      <div className="w-full md:mt-10 flex flex-row flex-wrap width-lim:flex-nowrap gap-2 md:gap-6">
        <ContainerCard
          className="
            w-full width-lim:max-w-[653px] width-lim:min-h-[635px] !px-0
            flex flex-col order-last width-lim:order-first
            "
        >
          <div className="px-4 md:px-8 pb-6 border-b border-pry-dark-green-400/5">
            {fetchingMembershipform || fetchingdefaultFormFields ? (
              <TitleSubLoader />
            ) : (
              <>
                <TitleSub
                  title="Set Registration Fee"
                  subtitle="This is the amount prospective members will be charged"
                  titleClassName=""
                  subtitleClassName=""
                  className=""
                />

                <NumericFormat
                  suffix={undefined}
                  prefix={undefined}
                  decimalScale={2}
                  thousandSeparator
                  allowLeadingZeros={false}
                  customInput={TextInput}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  name={"fee"}
                  value={values.fee}
                  placeholder="Input registration fee"
                  hasIcon={false}
                  iconSvg={undefined}
                  errors={errors?.fee && touched?.fee ? errors?.fee : ""}
                  inputClass=""
                  className="!pr-[16px]"
                  autoComplete="off"
                  customIcon={
                    <div>
                      <Icon svg={"naira"} width={20} height={20} />
                    </div>
                  }
                  containerClassName="mt-4"
                />
              </>
            )}
          </div>

          {/* For setup preview  */}
          <div className="px-4 md:px-8 pt-6 overflow-scroll flex flex-col">
            <TitleSub
              title="Membership Form"
              subtitle="Below are the fields(information) that will be collected from prospective members"
              titleClassName=""
              subtitleClassName=""
              className="order-1"
            />

            <div className="mt-[16px] gap-2 grid grid-cols-2 md:grid-cols-3 order-3 md:order-2">
              {fetchingMembershipform || fetchingdefaultFormFields ? (
                <>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((k) => {
                    return (
                      <Skeleton
                        width={"100%"}
                        height={60}
                        baseColor={SKELETON_BASE_COLOR}
                        highlightColor={SKELETON_HIGHLIGHT_COLOR}
                        key={`${k}`}
                        className="rounded-lg"
                      />
                    );
                  })}
                </>
              ) : (
                values.form?.map((field, idx) => {
                  const key = `membershipFormField-${idx}`;

                  return (
                    <FormCard
                      title={MEMBERSHIP_FORM_FIELD_TITLE_MAP[field.fieldType]}
                      text={splitCamelCase(field.question)}
                      icon={MEMBERSHIP_FORM_FIELD_ICONS_MAP[field.fieldType]}
                      mode="edit"
                      onRemove={
                        field.isDefault
                          ? undefined
                          : () => {
                              setFieldValue(
                                "form",
                                removeElementAtIndex(values.form, idx),
                              );
                            }
                      }
                      key={key}
                    />
                  );
                })
              )}
            </div>
          </div>

          {/* For adding new fields in edit mode  */}

          <div className="order-2 md:order-3">
            <AddFieldForm
              onSave={(v) => {
                setFieldValue("form", [v, ...values.form]);
              }}
            />
          </div>
        </ContainerCard>

        <InformationCard
          className="w-full width-lim:max-w-[345px] h-fit width-lim:sticky-top"
          info={
            <p>
              <span className="text-pry-green-200">Membership form</span> is
              what a prospective member will fill before joining your
              cooperative. You can make changes to the form here anytime
            </p>
          }
        />
      </div>

      <Button
        type="button"
        variation="primary"
        className="mt-4 md:hidden"
        text="Save"
        onClick={() => {
          handleSubmit();
        }}
        loading={updatingForm}
        disabled={updatingForm || !isValid}
      />
    </div>
  );
};
